import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Popper, Stack} from 'views/components';
import {PersonOutlineOutlined as PersonOutlineOutlinedIcon} from 'views/icons';
import {translate} from 'views/module';

import AdminTab from './Tabs/AdminTab';
import CaregiverTab from './Tabs/CaregiverTab';
import ClientTab from './Tabs/ClientTab';

const AssigneePicker = (props) => {
  const {
    assigneeCount,
    onSelectAssignee,
  } = props;

  const [isAssigneePickerOpen, setIsAssigneePickerOpen] = React.useState(false);
  const [assigneeTab, setAssigneeTab] = React.useState('admins');

  const assigneeButtonRef = React.useRef(null);

  const tabItems = [
    {value: 'admins', label: translate('tasks.Common.AssigneePicker.Tabs.Admin')},
    {value: 'caregivers', label: translate('tasks.Common.AssigneePicker.Tabs.Caregivers')},
    {value: 'clients', label: translate('tasks.Common.AssigneePicker.Tabs.Clients')},
  ];

  const handleSelectAssignee = React.useCallback((entityType, entity) => {
    onSelectAssignee(entityType, entity);
    setIsAssigneePickerOpen(false);
  }, [onSelectAssignee]);

  return (
    <>
      <Box ref={assigneeButtonRef} sx={{display: 'inline-flex'}}>
        <Button
          variant='outlined'
          onClick={() => setIsAssigneePickerOpen(true)}
          startIcon={<PersonOutlineOutlinedIcon sx={{fontSize: 16}} />}
          sx={{
            'borderColor': '#E8E4DE',
            'color': assigneeCount > 0 ? '#958ACC' : '#7A756E',
            'backgroundColor': 'transparent',
            'borderRadius': '8px',
            'textTransform': 'none',
            'minWidth': 0,
            'px': 1.5,
            'py': 0.5,
            '& .MuiButton-startIcon': {
              color: assigneeCount > 0 ? '#958ACC' : '#A8A49E',
            },
          }}
        >
          {`${translate('tasks.Common.AssigneePicker.Assignee')}${assigneeCount > 0 ? ` · ${assigneeCount}` : ''}`}
        </Button>
      </Box>
      <Popper
        open={isAssigneePickerOpen}
        anchorEl={assigneeButtonRef.current}
        placement='bottom-start'
        onClickAway={() => setIsAssigneePickerOpen(false)}
        popperModifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        zIndex={1400}
      >
        <Box
          sx={{
            mt: 1,
            width: 460,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 440,
            overflow: 'hidden',
            border: '1px solid #E8E4DE',
            borderRadius: '8px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 8px 24px rgba(25, 25, 25, 0.12)',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Stack direction='row' spacing={1} sx={{flexWrap: 'wrap'}}>
            {
              tabItems.map((tab) => (
                <Button
                  key={tab.value}
                  variant='outlined'
                  onClick={() => setAssigneeTab(tab.value)}
                  sx={{
                    borderRadius: '999px',
                    borderColor: assigneeTab === tab.value ? '#DA7955' : '#E8E4DE',
                    color: assigneeTab === tab.value ? '#DA7955' : '#7A756E',
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1.25,
                    py: 0.25,
                    fontSize: '13px',
                    lineHeight: 1.2,
                  }}
                >
                  {tab.label}
                </Button>
              ))
            }
          </Stack>
          {
            assigneeTab === 'admins' &&
            <AdminTab
              onSelectAssignee={handleSelectAssignee}
            />
          }
          {
            assigneeTab === 'caregivers' &&
            <CaregiverTab
              onSelectAssignee={handleSelectAssignee}
            />
          }
          {
            assigneeTab === 'clients' &&
            <ClientTab
              onSelectAssignee={handleSelectAssignee}
            />
          }
        </Box>
      </Popper>
    </>
  );
};

AssigneePicker.propTypes = {
  assigneeCount: PropTypes.number.isRequired,
  onSelectAssignee: PropTypes.func.isRequired,
};

export default React.memo(AssigneePicker);
